import { Box as MuiBox, css, styled } from "@mui/material";
import { Box, colors, Search } from "@procore/core-react";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { Events, track } from "apis/procore";
import { useAppSelector } from "app/redux-store-interactions";
import type { ListNode } from "components/objectTree/types";
import { collapseAllNodes } from "components/objectTree/utils";
import { SlottedWindow } from "components/window/SlottedWindow";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { highlightActions } from "contexts/highlighted";
import { useDispatchHighlighted, useHighlighted } from "contexts/highlighted";
import { useSetObjectTree } from "contexts/objectTree";
import type { BaseWindowProps } from "contexts/windows/reducer";
import { ModelTree } from "features/modelTree/components/modelTree";
import { emptyFileTypeFilters } from "features/modelTree/constants";
import type { ModelTreeNodeData } from "features/modelTree/types";
import { useAbortController } from "hooks/useAbortController";
import { MAX_SEARCH_RESULTS, useObjectTreeSearch } from "hooks/useObjectTreeSearch";
import { selectIsOnline } from "slices/explorer";
import { telemetry } from "telemetry";
import { selectObjectHierarchy } from "utils/objectActions";
import { getGroupStateForFilteredTree, getGroupStateForIdSet } from "utils/treeHelpers";

import { useBulkActionsContextMenu } from "../hooks/useBulkActionsContextMenu";
import { ObjectTreeControlsBar } from "./components/ObjectTreeControlsBar";
import { ObjectTreeNodeProcore } from "./components/ObjectTreeNodeProcore";
import { ITEM_HEIGHT } from "./constants";

const ObjectTreeWindowContent = styled(Box)(
    () => css`
        height: 100%;
        overflow: hidden;
        display: flex;
        flex-direction: column;

        .object-tree-scroll-container {
            overflow: auto;
            overscroll-behavior-x: contain;
            touch-action: pan-x pan-y;
            scrollbar-gutter: stable;
            scrollbar-color: transparent transparent;

            &:hover {
                scrollbar-color: ${colors.gray70} transparent;
            }
        }
    `,
);

export function ObjectTreeWindow({ onClose, position }: BaseWindowProps) {
    const { t } = useTranslation();
    const {
        state: { db },
    } = useExplorerGlobals();
    const isOnline = useAppSelector(selectIsOnline);
    const highlightedSet = useHighlighted();
    const dispatchHighlighted = useDispatchHighlighted();

    const {
        searchTerm,
        filteredObjectIds,
        tree,
        setTree,
        treeKey,
        isSearching,
        hasNoResults,
        searchError,
        isFilterActive,
        resultsTruncated,
        handleSearchChange,
        handleSearchSubmit,
    } = useObjectTreeSearch(db);

    const setTreeContext = useSetObjectTree();
    useEffect(() => {
        setTreeContext(tree);
    }, [tree, setTreeContext]);

    const [selectionAbortController] = useAbortController();

    const { openContextMenu, contextMenuNode } = useBulkActionsContextMenu();

    const onNodeRightClick = useCallback(
        (x: number, y: number, item: ListNode<ModelTreeNodeData>) => {
            const selected =
                filteredObjectIds != null
                    ? getGroupStateForFilteredTree(highlightedSet.ids, item, filteredObjectIds, tree)
                    : getGroupStateForIdSet(highlightedSet.ids, item, tree);

            const isAlreadySelected = selected !== "none";

            if (!isAlreadySelected && db) {
                dispatchHighlighted(highlightActions.setIds([]));

                const objects = item.nodeGroup?.map((n) => n.data.object) ?? [item.node.data.object];

                const selectionPromise = selectObjectHierarchy({
                    nodes: objects,
                    db,
                    abortSignal: selectionAbortController.current.signal,
                    setLoading: () => {},
                    dispatchHighlighted,
                });

                selectionPromise.catch((error) => {
                    if (!selectionAbortController.current.signal.aborted) {
                        console.error("Context menu selection failed:", error);
                        telemetry.trackCaughtException({
                            source: "objectTreeWindow.contextMenuSelection",
                            category: "data",
                            error,
                            severity: "error",
                        });
                    }
                });
            }

            openContextMenu(x, y);
        },
        [
            db,
            filteredObjectIds,
            tree,
            highlightedSet.ids,
            dispatchHighlighted,
            selectionAbortController,
            openContextMenu,
        ],
    );

    const handleCollapseAll = useCallback(() => {
        setTree((currentTree) => collapseAllNodes(currentTree));
        void track(Events.objectTree.collapseAll);
    }, [setTree]);

    const emptyState = !isOnline
        ? {
              title: t("noInternetConnection"),
              message: t("objectTreeOffline"),
              showIcon: false,
          }
        : searchError
          ? {
                title: t("objectTreeSearchError"),
                message: searchError,
            }
          : hasNoResults
            ? {
                  title: t("noResultsFound"),
                  message: t("tryDifferentSearchTerm"),
              }
            : undefined;

    return (
        <SlottedWindow
            position={position}
            size={{ width: 400, height: 600 }}
            loading={isSearching}
            loadingMessage={t("loadingResults")}
            emptyState={emptyState}
            onClose={onClose}
            headerRegion={<SlottedWindow.Title>{t("objectTree")}</SlottedWindow.Title>}
            subheaderRegion={
                <>
                    <SlottedWindow.SubheaderRow>
                        <Search
                            value={searchTerm}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            placeholder={t("filterByKeyword")}
                            autoComplete="off"
                            data-testid="object-tree-search"
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    void handleSearchSubmit();
                                }
                            }}
                        />
                    </SlottedWindow.SubheaderRow>
                    {isOnline && (
                        <SlottedWindow.BulkActionsRow active={highlightedSet.idArr.length > 0}>
                            <ObjectTreeControlsBar
                                onCollapseAll={handleCollapseAll}
                                tree={tree}
                                filteredObjectIds={filteredObjectIds}
                                hasNoResults={hasNoResults}
                                unwrapped
                            />
                        </SlottedWindow.BulkActionsRow>
                    )}
                </>
            }
            data-testid="model-tree-window"
        >
            <ObjectTreeWindowContent>
                {resultsTruncated && !isSearching && (
                    <MuiBox
                        sx={{ px: 2, py: 1, textAlign: "center", color: "text.secondary", fontSize: "0.875rem" }}
                        data-testid="search-results-truncated"
                    >
                        {t("searchResultsTruncated", { count: MAX_SEARCH_RESULTS })}
                    </MuiBox>
                )}
                <MuiBox sx={{ flex: "1 1 auto", minHeight: 0 }}>
                    <ModelTree
                        key={treeKey}
                        tree={tree}
                        setTree={setTree}
                        fileTypes={emptyFileTypeFilters}
                        NodeRenderer={ObjectTreeNodeProcore}
                        itemSize={ITEM_HEIGHT}
                        filteredObjectIds={filteredObjectIds}
                        searchTerm={searchTerm}
                        onNodeRightClick={onNodeRightClick}
                        isFilterActive={isFilterActive}
                    />
                </MuiBox>
            </ObjectTreeWindowContent>

            {contextMenuNode}
        </SlottedWindow>
    );
}
